import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { appFocusPatientOnDashboard } from "../core/app-bridge.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { usePractice } from "../core/providers/practice.provider.tsx";
import type { Patient } from "../core/services/patients.service.ts";
import { patientsService } from "../core/services/patients.service.ts";
import {
  appointmentView,
  appointmentsForDate,
  type AgendaAppointment,
} from "../agenda/appointments.ts";
import {
  addDays,
  formatHour,
  isSameDay,
  todayLocal,
} from "../agenda/date-utils.ts";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { DateStepper } from "../shared/selection-nav.component.tsx";

type SlotStatus = "done" | "current" | "upcoming";

function appointmentStatus(
  event: { start: number; dur: number },
  isToday: boolean,
): SlotStatus {
  if (!isToday) return "upcoming";
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  if (event.start + event.dur <= hour) return "done";
  if (event.start <= hour) return "current";
  return "upcoming";
}

function patientForSlot(
  patientUuid: string,
  practiceId: string,
): Patient | undefined {
  return patientsService.findByUuid(patientUuid, practiceId);
}

export function PatientsTodayWidget() {
  const { practice } = usePractice();
  const { current } = Interactions.usePatient();
  const [selectedDate, setSelectedDate] = React.useState(() => todayLocal());
  const today = todayLocal();
  const rawAppointments = appointmentsForDate(selectedDate, practice.uuid);
  const slots = rawAppointments.map((a) => appointmentView(a));
  const dateLabel = selectedDate.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const isViewingToday = isSameDay(selectedDate, today);

  const rowClass: Record<SlotStatus, string> = {
    done: "bg-neutral-50 border border-transparent",
    current: "bg-sf-primary-50 border border-sf-primary/20",
    upcoming: "bg-white border border-transparent",
  };

  const textClass: Record<SlotStatus, string> = {
    done: "text-sf-text-muted",
    current: "text-sf-text",
    upcoming: "text-sf-text",
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p
          className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs capitalize text-sf-text-muted"
          title={`${dateLabel} · ${slots.length} rendez-vous`}
        >
          {dateLabel}
          <span className="text-sf-text-muted"> · </span>
          <span className="font-semibold text-sf-text">
            {slots.length} rendez-vous
          </span>
        </p>
        <DateStepper
          onPrev={() => setSelectedDate((date) => addDays(date, -1))}
          onNext={() => setSelectedDate((date) => addDays(date, 1))}
          onToday={() => setSelectedDate(todayLocal())}
          todayActive={isViewingToday}
        />
      </div>
      {slots.length === 0 && (
        <div className="rounded-md border border-dashed border-sf-border px-3 py-4 text-center text-xs text-sf-text-muted">
          Aucun rendez-vous planifié ce jour.
        </div>
      )}
      {rawAppointments.map((appt: AgendaAppointment, i) => {
        const s = slots[i]!;
        const patient = patientForSlot(appt.patientUuid, practice.uuid);
        const st = appointmentStatus(s, isViewingToday);
        const isSelected = patient != null && current?.uuid === patient.uuid;
        return (
          <button
            key={appt.id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => patient && appFocusPatientOnDashboard(patient)}
            disabled={!patient}
            className={cn(
              "grid w-full cursor-pointer grid-cols-[54px_36px_1fr_auto] items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors",
              rowClass[st],
              isSelected && "ring-2 ring-sf-primary ring-offset-1",
              !patient && "cursor-default opacity-60",
            )}
          >
            <div
              className={cn(
                "text-xs font-semibold tabular-nums",
                textClass[st],
              )}
            >
              {formatHour(s.start)}
            </div>
            {patient ? (
              <PatientAvatar patient={patient} size={32} />
            ) : (
              <span
                className="size-8 shrink-0 rounded-full"
                style={{ background: s.color }}
              />
            )}
            <div className="min-w-0">
              <div
                className={cn(
                  "truncate text-[13px]",
                  textClass[st],
                  st === "current" ? "font-semibold" : "font-medium",
                )}
              >
                {s.patient}
              </div>
              {s.motif && (
                <div className="truncate text-[11.5px] text-sf-text-muted">
                  {s.motif}
                </div>
              )}
            </div>
            <div className="text-[11px] text-sf-text-muted">
              {Math.round(s.dur * 60)}min
            </div>
          </button>
        );
      })}
    </div>
  );
}
